// Purpose: route table.
//
// Route structure:
//   /login                   — standalone, full-bleed
//   /                        — AppShell (sidebar + main) for all
//                              authenticated pages, including /admin/*
//   /s/:token, /d/:token     — ExternalShell (full-bleed atmospheric
//                              chrome, no sidebar)
//
// AppShell renders the sidebar context-aware (Admin group appears for
// ADMIN users). The RequireAdmin guard still wraps the /admin routes
// for the server-enforced gate — UI visibility alone is not security.

import { Route, Routes } from 'react-router-dom';
import { AppShell } from './layout/AppShell.tsx';
import { ExternalShell } from './layout/ExternalShell.tsx';
import { RequireAdmin } from './components/RequireAdmin.tsx';
import { RequireAuth } from './auth/RequireAuth.tsx';
import { Landing } from './pages/Landing.tsx';
import { Login } from './pages/Login.tsx';
import { Share } from './pages/Share.tsx';
import { NotFound } from './pages/NotFound.tsx';
import { Shares } from './pages/Shares.tsx';
import { NewShare } from './pages/NewShare.tsx';
import { ShareDetail } from './pages/ShareDetail.tsx';
import { RecipientLanding } from './pages/RecipientLanding.tsx';
import { RecipientFiles } from './pages/RecipientFiles.tsx';
import { Dropboxes } from './pages/Dropboxes.tsx';
import { NewEphemeralDropbox } from './pages/NewEphemeralDropbox.tsx';
import { NewPersistentDropbox } from './pages/NewPersistentDropbox.tsx';
import { DropboxDetailPage } from './pages/DropboxDetail.tsx';
import { DropboxUpload } from './pages/DropboxUpload.tsx';
import { AdminApprovals } from './pages/admin/Approvals.tsx';
import { Dashboard } from './pages/admin/Dashboard.tsx';
import { Audit } from './pages/admin/Audit.tsx';
import { Routing } from './pages/admin/Routing.tsx';
import { Forensics } from './pages/admin/Forensics.tsx';
import { ForensicsView } from './pages/admin/ForensicsView.tsx';
import { Users } from './pages/admin/Users.tsx';
import { Settings } from './pages/admin/Settings.tsx';
import { Notice } from './pages/Notice.tsx';

export function App() {
  return (
    <Routes>
      {/* Sign-in: standalone, full-bleed. */}
      <Route path="login" element={<Login />} />

      {/* External flows: no sidebar, atmospheric chrome. */}
      <Route element={<ExternalShell />}>
        <Route path="d/:token" element={<DropboxUpload />} />
        <Route path="s/:token" element={<RecipientLanding />} />
        <Route path="s/:token/files" element={<RecipientFiles />} />
        <Route path="s/:token/legacy" element={<Share />} />
        <Route path="notice" element={<Notice />} />
      </Route>

      {/* Authenticated app: RequireAuth gates everything below. The
          outer AppShell renders the sidebar + workspace chrome only
          for signed-in users; unauthenticated visitors hitting any
          of these routes get <Navigate to="/login" />. */}
      <Route element={<RequireAuth />}>
        <Route element={<AppShell />}>
          <Route index element={<Landing />} />
          <Route path="shares" element={<Shares />} />
          <Route path="shares/new" element={<NewShare />} />
          <Route path="shares/:id" element={<ShareDetail />} />
          <Route path="dropboxes" element={<Dropboxes />} />
          <Route path="dropboxes/new/ephemeral" element={<NewEphemeralDropbox />} />
          <Route path="dropboxes/new/persistent" element={<NewPersistentDropbox />} />
          <Route path="dropboxes/:id" element={<DropboxDetailPage />} />
          <Route element={<RequireAdmin />}>
            <Route path="admin">
              <Route index element={<Dashboard />} />
              <Route path="approvals" element={<AdminApprovals />} />
              <Route path="audit" element={<Audit />} />
              <Route path="routing" element={<Routing />} />
              <Route path="forensics" element={<Forensics />} />
              <Route path="forensics/view" element={<ForensicsView />} />
              <Route path="users" element={<Users />} />
              <Route path="settings" element={<Settings />} />
            </Route>
          </Route>
          <Route path="*" element={<NotFound />} />
        </Route>
      </Route>
    </Routes>
  );
}
